import React, {
    CSSProperties,
    ReactNode, useEffect, useRef, useState
} from 'react'
import styled, { css } from 'styled-components'
import get from 'lodash/get'
import { Checkbox } from 'components'
import { colors } from 'utils'
import { RequestStatus } from 'types'
import FullPageLoading from './FullPageLoading'
import Icon from './Icon'
import NoData from './NoData'

const S = {
    Wrapper: styled.div`
        width: 100%;
    `,
    Row: styled.div<{ theme: string, isMultiSelect?: boolean }>`
        display: flex;
        align-items: center;
        margin-bottom: 2px;
        padding: 8px;
        background-color: ${colors.darkOpacity(0.05)};
        color: ${colors.grayTitleOpacity(0.8)};
        cursor: pointer;
        &:hover > div[data-action-column="true"] {
            display: flex;
        }
        ${({ theme }) => theme === 'light' && css`
            background-color: white;
            padding-top: 0;
            padding-bottom: 0;
            margin-bottom: 0;
            &:hover {
                background-color: ${colors.iconOpacity(0.03)};
            }
        `}
    `,
    Col: styled.div<{ $isActionCol: boolean, $hideAction: boolean }>`
        flex-shrink: 0;
        padding: 0 4px;
        font-size: 14px;
        ${({ $isActionCol, $hideAction }) => ($isActionCol && ($hideAction ? css`
            display: none;
            justify-content: flex-end;
        ` : css`
            display: flex;
            justify-content: flex-end;
        `))}
    `,
    Header: styled.div<{ sortable?: boolean }>`
        height: 40px;
        display: flex;
        flex-shrink: 0;
        padding: 0 4px;
        align-items: center;
        color: ${colors.icon};
        font-weight: 700;
        cursor: ${({ sortable }) => (sortable ? 'pointer' : 'auto')};
        user-select: none;
        ${({ theme }) => theme === 'light' && css`
            background-color: transparent;
        `}
    `,
    SortIcon: styled(Icon)`
        margin: 0 0 10px 10px;
        transform: scaleY(2);
    `,
    Checkbox: styled(Checkbox)`
        padding-left: 20px;
        margin-left: 10px;
    `
}

export interface ColumnDataGrid {
    header?: string
    name: string
    width?: number
    minWidth?: number
    render?: (row) => ReactNode
    sortable?: boolean
    style?: CSSProperties
}

export interface DataGridProps {
    data: Record<string, any>[]
    columns: ColumnDataGrid[]
    hideHeader?: boolean
    hideAction?: boolean
    fetchMoreData?: () => void
    hasMore?: boolean
    scrolled?: boolean
    onScroll?: () => void
    theme?: 'light' | 'gray'
    onRowClick?: (e?: any) => void
    onSort?: (e?: any) => void
    className?: string
    requestStatus?: RequestStatus
    totalCols?: number
    isMultiSelect?: boolean
    onGetSelectedIds?: (data) => void
}

type SortingOrder = 'Asc' | 'Desc'

const DataGrid = ({
    data = [],
    columns = [],
    hideHeader = true,
    hideAction,
    scrolled,
    theme,
    onRowClick = () => null,
    onSort = () => null,
    className,
    requestStatus,
    totalCols=12,
    isMultiSelect,
    onGetSelectedIds
}: DataGridProps) => {
    const endRef = useRef<HTMLDivElement>()
    const [sortingField, setSortingField] = useState('')
    const [sortingOrder, setSortingOrder] = useState<SortingOrder>('Asc')
    const [selectedIds, setSelectedIds] = useState({})

    useEffect(() => {
        // To fix issue auto scroll to top when reaching the end of list. (ORG-1367)
        if (endRef.current && scrolled) {
            endRef.current.scrollIntoView()
        }
    })

    const handleSort = (name: string) => {
        if (sortingField !== name) {
            setSortingField(name)
            setSortingOrder('Asc')
            onSort(`${name}Asc`)
            return
        }
        if (sortingOrder === 'Asc') {
            setSortingOrder('Desc')
            onSort(`${name}Desc`)
            return
        }

        setSortingField('')
        onSort('')
    }

    const renderSortingIcon = (column: ColumnDataGrid) => {
        if (column.name !== sortingField || !column.sortable) {
            return
        }
        return (
            <S.SortIcon icon={sortingOrder === 'Asc' ? 'arrowUp' : 'arrowDown'} size={9} />
        )
    }

    const handleSelectRow = (id) => (event) => {
        const _selectedIds = { ...selectedIds }
        event.stopPropagation()
        if (selectedIds[id]) {
            delete _selectedIds[id]
        } else {
            _selectedIds[id] = id
        }
        setSelectedIds(_selectedIds)
        onGetSelectedIds(Object.values(_selectedIds))
    }

    if (requestStatus === 'succeeded' && !data.length) {
        return (
            <NoData />
        )
    }

    return (
        <S.Wrapper className={className}>
            {!hideHeader && (
                <S.Row theme={theme} className="datagrid__row-header" isMultiSelect={isMultiSelect}>
                    <>
                        {isMultiSelect && <div style={{ minWidth: 29 }} />}
                        {
                            columns.map((column, index) => (
                                <S.Header
                                    className="datagrid__column-header text-cut"
                                    style={{
                                        minWidth: column.minWidth,
                                        width: `
                                            calc(${(column.width/totalCols)*100}% - ${isMultiSelect ? (column.width*30)/totalCols : 0}px)
                                        `,
                                    }}
                                    key={index}
                                    theme={theme}
                                    sortable={column.sortable}
                                    onClick={() => column.sortable && handleSort(column.name)}
                                >
                                    {column.header}
                                    {renderSortingIcon(column)}
                                </S.Header>
                            ))
                        }
                    </>
                </S.Row>
            )}

            {data.map((row, i) => (
                <S.Row
                    key={i}
                    theme={theme}
                    onClick={() => onRowClick(row)}
                    className="datagrid__row-item"
                    isMultiSelect={isMultiSelect}
                >
                    {isMultiSelect && (
                        <div onClick={handleSelectRow(row.id)}>
                            <S.Checkbox
                                label=""
                                checked={selectedIds[row.id]}
                            />
                        </div>
                    )}
                    {
                        columns.map((column, index) => (
                            <S.Col
                                key={index}
                                $isActionCol={column.name === 'actions'}
                                $hideAction={hideAction}
                                data-action-column={column.name === 'actions'}
                                style={{
                                    minWidth: column.minWidth,
                                    width: `
                                        calc(${(column.width/totalCols)*100}% - ${isMultiSelect ? (column.width*30)/totalCols : 0}px)
                                    `,
                                    ...column.style
                                }}
                                onClick={(e) => {
                                    if (column.name === 'actions') {
                                        e.stopPropagation()
                                    }
                                }}
                            >
                                {column.render?.(row) || get(row, column.name)}
                            </S.Col>
                        ))
                    }
                </S.Row>
            ))}
            {requestStatus === 'loading' && <FullPageLoading isAbsolute />}
        </S.Wrapper>
    )
}

export default DataGrid
